import { GQL } from '@aline/graphql';

/** The shape any launch surface needs to decide whether to ask a field. */
export interface LaunchFieldVisibility {
  /** The launch-form field whose answer decides whether this one is asked. */
  fieldId: string;
  /** Values of that field which reveal this one. */
  showForValues: string[];
}

export interface GatedLaunchField {
  fieldId: string;
  /** A yes/no field (`CHECKBOX`) reads an unanswered value as "false". */
  type?: string | null;
  visibility?: LaunchFieldVisibility | null;
}

/**
 * Which launch-form fields to hide, given the answers so far.
 *
 * A field that lives inside a dynamic block's choice is only asked while that
 * choice is selected. The launched document will not contain it otherwise. Drivers nest, so this resolves to a fixed point and treats a
 * condition on a *hidden* driver as unsatisfied: a question nobody is being
 * asked cannot reveal anything.
 *
 * A driver that is not on the launch form at all is the opposite case. Its
 * question is never asked, so the condition can never be met, and hiding on
 * that basis would permanently drop a field the author opted in on its own.
 * So a gate that cannot be evaluated does not hide.
 *
 * Shared by every launch surface (the quickstart, the launch config form, the
 * chat card, and the Slack/Teams modals) so none of them can drift into asking
 * for a variant the launcher did not pick.
 */
export function getHiddenLaunchFieldIds<TField extends GatedLaunchField>(
  fields: TField[],
  valueByFieldId: ReadonlyMap<string, string>
): Set<string> {
  const gated = fields.filter(field => field.visibility);
  if (gated.length === 0) return new Set<string>();

  const askedFieldIds = new Set(fields.map(field => field.fieldId));
  const checkboxFieldIds = new Set(
    fields.flatMap(field => (field.type === GQL.AlineFieldType.Checkbox ? [field.fieldId] : []))
  );

  const step = (current: ReadonlySet<string>): Set<string> => {
    const next = new Set<string>();
    for (const field of gated) {
      const gate = field.visibility;
      if (!gate) continue;
      if (!askedFieldIds.has(gate.fieldId)) continue;
      if (current.has(gate.fieldId)) {
        next.add(field.fieldId);
        continue;
      }
      const selected = getDriverAnswer({
        isCheckbox: checkboxFieldIds.has(gate.fieldId),
        value: valueByFieldId.get(gate.fieldId),
      });
      if (gate.showForValues.includes(selected)) continue;
      next.add(field.fieldId);
    }
    return next;
  };

  let hidden = new Set<string>();
  for (let pass = 0; pass <= gated.length; pass++) {
    const next = step(hidden);
    if (next.size === hidden.size && [...next].every(id => hidden.has(id))) return hidden;
    hidden = next;
  }

  // Unsettled: hide only what survives another step, so oscillating names show.
  const oneMoreStep = step(hidden);
  return new Set([...hidden].filter(id => oneMoreStep.has(id)));
}

/**
 * The fields to ask, given the answers so far. The Slack modal cannot
 * re-evaluate itself, so it calls this again on every refresh with the answers
 * it currently holds, which is how a variant's questions appear there. Server
 * validation uses it to demand only the questions the answers call for.
 */
export function getAskableLaunchFields<TField extends GatedLaunchField>(
  fields: TField[],
  valueByFieldId: ReadonlyMap<string, string> = new Map()
): TField[] {
  const hidden = getHiddenLaunchFieldIds(fields, valueByFieldId);
  return fields.filter(field => !hidden.has(field.fieldId));
}

/**
 * The answers with each untouched yes/no driver set to "false". A surface that
 * shows a yes/no question as a Yes/No dropdown (Slack, the Teams and Email
 * page) preselects "No" with this, so the dropdown says what the form already
 * assumes: an unchecked box on the web, and the "false" launch writes.
 */
export function withYesNoDriverDefaults<TField extends GatedLaunchField>(
  fields: TField[],
  values: Record<string, string>
): Record<string, string> {
  const driverIds = new Set(fields.flatMap(field => field.visibility?.fieldId ?? []));
  const untouched = fields.filter(
    field =>
      field.type === GQL.AlineFieldType.Checkbox &&
      driverIds.has(field.fieldId) &&
      !values[field.fieldId]?.trim()
  );
  if (!untouched.length) return values;
  const next = { ...values };
  for (const field of untouched) next[field.fieldId] = 'false';
  return next;
}

/**
 * A yes/no driver renders as a checkbox, and an untouched box reads as "No".
 * Treating its empty value as "false" keeps the "No" variant's questions in
 * step with what the box shows. Launch writes that same "false" so the
 * document picks the matching variant.
 */
function getDriverAnswer({ isCheckbox, value = '' }: { isCheckbox: boolean; value?: string }) {
  if (!isCheckbox) return value;
  return value.trim().toLowerCase() || 'false';
}
